/**
 * Motion for Continuance + Order generation (Lloyd, Aug 19-25 2026 — Section D).
 *
 * Same split as the appeal bond, and for the same reason: `readiness` asks what is missing WITHOUT
 * producing anything, so the picker can show blockers as staff choose a ground and a setting date.
 * Only `generate` produces documents, and it always produces both the motion and the order together
 * — the API has no shape that returns one without the other.
 */

export type ContinuanceGround =
  | 'NO_LOCAL_COVERAGE'
  | 'CONFLICTING_SETTING'
  | 'ADDITIONAL_PREPARATION_TIME';

export const CONTINUANCE_GROUND_OPTIONS: Array<{
  value: ContinuanceGround;
  label: string;
  pendingConfirmation: boolean;
}> = [
  {
    value: 'NO_LOCAL_COVERAGE',
    label: 'Counsel could not secure local coverage for the setting',
    pendingConfirmation: false,
  },
  {
    value: 'CONFLICTING_SETTING',
    label: 'Counsel has a conflicting setting in another court',
    pendingConfirmation: true,
  },
  {
    value: 'ADDITIONAL_PREPARATION_TIME',
    label: 'Additional time is needed to review discovery or prepare',
    pendingConfirmation: true,
  },
];

export interface ContinuanceBlockerDto {
  field: string | null;
  reason: string;
}

export interface ContinuanceReadinessDto {
  canGenerate: boolean;
  blockers: ContinuanceBlockerDto[];
}

export interface GenerateContinuanceMotionInput {
  ground: ContinuanceGround | string;
  settingDate: string;
  servedMethod: string;
  servedOn: string;
  servedAt?: string | null;
  signatureDate?: string | null;
  courtName?: string | null;
}

export interface ContinuanceDocumentDto {
  fileName: string;
  pdfBase64: string;
}

export interface ContinuanceMotionResultDto {
  motion: ContinuanceDocumentDto;
  order: ContinuanceDocumentDto;
}

/** Thrown when the case (or the service facts) is not ready. Carries the reasons so the caller can list them. */
export class ContinuanceMotionBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: ContinuanceBlockerDto[],
  ) {
    super(message);
    this.name = 'ContinuanceMotionBlockedError';
  }
}

export default class ContinuanceMotionApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async readiness(
    ticketGuid: string,
    params: {
      ground?: string;
      settingDate?: string;
      servedAt?: string;
      servedMethod?: string;
      servedOn?: string;
      courtName?: string;
    },
  ): Promise<ContinuanceReadinessDto> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, value);
    }
    const queryString = query.toString();
    const response = await fetch(
      `${ContinuanceMotionApi.host()}/api/v1/manager/ticket/${ticketGuid}/continuance-motion/readiness${
        queryString ? `?${queryString}` : ''
      }`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      throw new Error('Failed to check whether this case is ready for a motion for continuance');
    }
    const body = await response.json();
    return body.readiness;
  }

  static async generate(
    ticketGuid: string,
    input: GenerateContinuanceMotionInput,
  ): Promise<ContinuanceMotionResultDto> {
    const response = await fetch(
      `${ContinuanceMotionApi.host()}/api/v1/manager/ticket/${ticketGuid}/continuance-motion`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      throw new ContinuanceMotionBlockedError(
        err.message || 'This case is not ready for a motion for continuance.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.details || err.error || err.message || 'Failed to generate the motion for continuance',
      );
    }
    return response.json();
  }
}
